import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
// The design's own calendar, from Hugeicons' stroke-rounded set — see
// `.claude/skills/hugeicons`. It replaces lucide's `Calendar` here because
// this one glyph is drawn on every date field in the app and, at `iconOnly`,
// is a control in its own right.
import { CalendarIcon } from "./Icons";
import useAnchoredPanel, {
  anchoredPanelStyle,
  dismissKeyboard,
} from "./useAnchoredPanel";
import { useIsMobile } from "./useIsMobile";

/**
 * A date field that can be typed into or picked from a calendar.
 *
 * Ported from the VAV central ERP's `DatePicker`, keeping the part that makes
 * it work for a date of birth: the header drills down. The month opens a grid
 * of months and the year opens a page of twenty years, so a date decades back
 * is three clicks rather than four hundred presses of a back arrow. What is
 * not carried over is its inline styling — this one is built out of the same
 * Tailwind, lucide icons and portalled panel as `CustomDropdown` beside it.
 *
 * The value is the app's own `DD/MM/YYYY`, not an ISO string: that is what the
 * form holds, what its validation reads, and what the field displays, so the
 * calendar is an alternative way of filling the same box rather than a second
 * format to convert between.
 */

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTHS_SHORT = MONTHS.map((month) => month.slice(0, 3));

/** How many years the year grid shows at once. */
const YEAR_PAGE = 20;

const PANEL_WIDTH = 288;
/** Only used to decide whether the panel has room below its field. */
const PANEL_HEIGHT = 348;

type PanelView = "days" | "months" | "years";

/** Midnight, so two dates compare on the day rather than the moment. */
const atMidnight = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * `DD/MM/YYYY` into a date, or `null` for anything part-typed or impossible.
 * Reading the parts back off is what rejects 31/02 — `new Date(2026, 1, 31)`
 * rolls forward to March rather than failing.
 */
export const parseDisplayDate = (value: string): Date | null => {
  const match = DATE_PATTERN.exec((value ?? "").trim());
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return atMidnight(date);
};

/** A date back into the `DD/MM/YYYY` the field holds. */
export const formatDisplayDate = (date: Date): string =>
  [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getFullYear()),
  ].join("/");

export interface DatePickerProps {
  /** `DD/MM/YYYY`, or empty. */
  value: string;
  /** Fires for both a typed keystroke and a picked day. */
  onValueChange: (value: string) => void;
  /** Runs the typed text through the form's normaliser before it lands. */
  normalise?: (raw: string) => string;
  placeholder?: string;
  /** Marks the field visited — fires when the panel closes, however it closed. */
  onBlur?: () => void;
  invalid?: boolean;
  disabled?: boolean;
  /** Days outside these are shown but cannot be picked. */
  minDate?: Date;
  maxDate?: Date;
  id?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  /**
   * Drops the text field, leaving the calendar button as the whole control.
   *
   * For a date that is a place to go rather than an answer to give — My task's
   * day strip, where the seven pills beside it already say which day is being
   * looked at and a field repeating it in digits would be saying it twice. The
   * panel, the drill-down and the min/max are all unchanged: this is the same
   * picker with its typing half taken off.
   *
   * Not a general "compact" mode. Anywhere a date is being *entered* the field
   * stays, because a date of birth is quicker typed than hunted for.
   *
   * The button fills whatever `className` sizes the wrapper to, so a caller
   * standing it in a row of other controls can give it their height and know
   * the two agree. Left unsized it takes its own height from the glyph and its
   * padding, which is the sensible default on its own.
   */
  iconOnly?: boolean;
  /** What the icon-only button is called. Ignored otherwise. */
  label?: string;
  /**
   * Draws a "Go to today" row under the calendar, which picks today and shuts
   * the panel.
   *
   * Opt-in rather than always on, because it is only worth anything where the
   * panel is a way of *moving* — My task's day strip, which can be walked a
   * month out and then has no one press that comes back. A form asking for a
   * date of birth has nothing to come back to, and a shortcut nobody could
   * want is a row of the panel spent saying so.
   *
   * It takes itself away when today falls outside `minDate`/`maxDate`: a
   * shortcut that cannot be taken is worse than no shortcut, since the only
   * way to find that out is to press it.
   */
  showToday?: boolean;
  className?: string;
}

const navButtonClasses =
  // Thumb-sized below `md`, where the calendar is a full-width sheet and
  // paging a month is a press rather than a click.
  "flex h-7 w-7 max-md:h-10 max-md:w-10 flex-shrink-0 items-center justify-center rounded-md border " +
  "border-line bg-white text-ink-muted transition-colors hover:bg-[#F4F7F8] " +
  "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white";

/** The month/year buttons in the header, and every cell of the two grids. */
const headerLabelClasses =
  "rounded-md px-2 py-0.5 max-md:px-3 max-md:py-1.5 text-[14px] max-md:text-[17px] " +
  "font-medium transition-colors hover:bg-brand-soft";

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onValueChange,
  normalise,
  placeholder = "DD/MM/YYYY",
  onBlur,
  invalid = false,
  disabled = false,
  minDate,
  maxDate,
  showToday = false,
  id,
  iconOnly = false,
  label = "Open calendar",
  className = "",
  ...aria
}) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<PanelView>("days");

  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  /**
   * Set for the instant the panel is being opened in, so the blur that takes
   * the keyboard down is not mistaken for the field being left.
   *
   * `openPanel` blurs the box before it measures anything — see there — and
   * the box reports its own blur to the form, which is how a field is marked
   * touched. Without this, reaching for the calendar is what turns the field
   * red: "Date is required", under a panel opened to answer it.
   */
  const opening = useRef(false);

  const { rect, openUpward } = useAnchoredPanel(anchorRef, open, PANEL_HEIGHT);

  const selected = useMemo(() => parseDisplayDate(value), [value]);

  /**
   * Which of the two shapes the calendar opens in — see `panel`, where the
   * choice is argued. Declared up here because the body-scroll lock below
   * needs it too, and a hook cannot be reached for halfway down a render.
   */
  const isMobile = useIsMobile();

  /**
   * Where the calendar opens when the field is empty: the latest date that can
   * be picked, so a date of birth starts at the newest allowed year rather
   * than at today and a page of years that are all out of range.
   */
  const fallbackMonth = useMemo(
    () => atMidnight(maxDate ?? new Date()),
    [maxDate]
  );

  const [month, setMonth] = useState(() => {
    const from = selected ?? fallbackMonth;
    return new Date(from.getFullYear(), from.getMonth(), 1);
  });
  const [yearStart, setYearStart] = useState(
    () =>
      Math.floor((selected ?? fallbackMonth).getFullYear() / YEAR_PAGE) *
      YEAR_PAGE
  );

  const close = useCallback(() => {
    setOpen(false);
    setView("days");
    onBlur?.();
  }, [onBlur]);

  /** Opens on the month the field already names, wherever it was left last. */
  const openPanel = useCallback(() => {
    if (disabled) return;
    /*
     * Put the keyboard away before anything is measured, and this is what
     * decides where the calendar lands rather than being a courtesy.
     *
     * The field itself is typed into, so on a phone it keeps the caret when
     * the calendar button beside it is pressed — and a browser that will not
     * report its keyboard has `visibleBand` assume one is covering half the
     * screen for as long as something is focused. `anchoredPanelStyle` clamps
     * the trigger into that band before it places anything, so a date field in
     * the lower half of the screen was measured as though it were at the
     * halfway line: the calendar opened against the middle of the window, a
     * clear two hundred pixels above the field it belongs to, with the body of
     * whatever it was opened over showing between the two. On a date field
     * near the foot of a bottom sheet it landed on the sheet's own title.
     *
     * Blurred, there is no assumed keyboard, the band is the window, and the
     * panel hangs off the field the way it does on a desktop. From inside the
     * gesture that opens the panel, which is what a phone browser asks for
     * before it will move a keyboard either way. See `dismissKeyboard`.
     */
    opening.current = true;
    dismissKeyboard();
    opening.current = false;
    const from = parseDisplayDate(value) ?? fallbackMonth;
    setMonth(new Date(from.getFullYear(), from.getMonth(), 1));
    setYearStart(Math.floor(from.getFullYear() / YEAR_PAGE) * YEAR_PAGE);
    setView("days");
    setOpen(true);
  }, [disabled, value, fallbackMonth]);

  // A pointer press outside both the field and the panel dismisses it.
  // `mousedown` rather than `click`, so the panel is gone before a click lands
  // on whatever was underneath it.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !anchorRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        close();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open, close]);

  // Nothing scrolls behind the phone's sheet. Only there: the desktop panel
  // hangs off its field and is re-measured as the page moves under it, so
  // locking the page would be taking something away for no reason.
  const sheetOpen = open && isMobile;
  useEffect(() => {
    if (!sheetOpen) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [sheetOpen]);

  const outOfRange = (date: Date): boolean => {
    const day = atMidnight(date);
    if (minDate && day < atMidnight(minDate)) return true;
    if (maxDate && day > atMidnight(maxDate)) return true;
    return false;
  };

  const pick = (date: Date) => {
    onValueChange(formatDisplayDate(date));
    close();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape" && open) {
      event.stopPropagation();
      close();
      return;
    }
    // The keyboard's version of reaching for the calendar.
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      // Enter in a form field submits it; only the arrow opens the panel.
      if (event.key === "ArrowDown") {
        event.preventDefault();
        openPanel();
      }
    }
  };

  /* ── The three views ───────────────────────────────────────────────────── */

  const renderDays = () => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const leading = new Date(year, monthIndex, 1).getDay();
    const today = atMidnight(new Date());

    const cells: React.ReactNode[] = [];
    for (let blank = 0; blank < leading; blank += 1) {
      cells.push(<span key={`blank-${blank}`} aria-hidden="true" />);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, monthIndex, day);
      const unavailable = outOfRange(date);
      const isSelected = !!selected && selected.getTime() === date.getTime();
      const isToday = today.getTime() === date.getTime();

      cells.push(
        <button
          key={day}
          type="button"
          disabled={unavailable}
          aria-pressed={isSelected}
          aria-label={`${day} ${MONTHS[monthIndex]} ${year}`}
          onClick={() => pick(date)}
          // Sized to the column below `md` rather than to a fixed 32px: the
          // panel is the width of the phone there, and a grid of 32px circles
          // inside it is a desktop calendar with 200px of white either side
          // of it. `aspect-square` keeps the circle a circle whatever the
          // seventh of the screen works out to, and the cap stops a tablet in
          // portrait drawing seven saucers.
          className={`mx-auto flex h-8 w-8 max-md:h-auto max-md:w-full max-md:max-w-[46px] max-md:aspect-square items-center justify-center rounded-full text-[13px] max-md:text-[15px] transition-colors disabled:cursor-not-allowed disabled:text-ink-subtle disabled:opacity-50 ${
            isSelected
              ? "bg-brand font-semibold text-white"
              : isToday
              ? "border border-brand font-semibold text-brand-dark hover:bg-brand-soft"
              : "text-ink hover:bg-brand-soft"
          }`}
        >
          {day}
        </button>
      );
    }

    return (
      <>
        <div className="mb-1 grid grid-cols-7 gap-1 max-md:gap-x-0.5">
          {WEEKDAYS.map((weekday) => (
            <span
              key={weekday}
              aria-hidden="true"
              className="flex h-7 max-md:h-9 items-center justify-center text-[11px] max-md:text-[12px] font-medium text-ink-muted"
            >
              {weekday}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 max-md:gap-x-0.5 max-md:gap-y-1.5">{cells}</div>
      </>
    );
  };

  const renderMonths = () => (
    <div className="grid grid-cols-4 gap-1.5 max-md:gap-2">
      {MONTHS_SHORT.map((short, index) => {
        const active = index === month.getMonth();
        return (
          <button
            key={short}
            type="button"
            aria-label={MONTHS[index]}
            aria-pressed={active}
            onClick={() => {
              setMonth(new Date(month.getFullYear(), index, 1));
              setView("days");
            }}
            className={`rounded-md py-2 max-md:py-3.5 text-[13px] max-md:text-[15px] transition-colors ${
              active
                ? "bg-brand-soft font-semibold text-brand-dark"
                : "text-ink hover:bg-brand-soft"
            }`}
          >
            {short}
          </button>
        );
      })}
    </div>
  );

  const renderYears = () => (
    <div className="grid grid-cols-4 gap-1.5 max-md:gap-2">
      {Array.from({ length: YEAR_PAGE }, (_, index) => yearStart + index).map(
        (year) => {
          const active = year === month.getFullYear();
          return (
            <button
              key={year}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setMonth(new Date(year, month.getMonth(), 1));
                setView("months");
              }}
              className={`rounded-md py-2 max-md:py-3.5 text-[13px] max-md:text-[15px] transition-colors ${
                active
                  ? "bg-brand-soft font-semibold text-brand-dark"
                  : "text-ink hover:bg-brand-soft"
              }`}
            >
              {year}
            </button>
          );
        }
      )}
    </div>
  );

  /** Back and forward mean a month, a year or a page of years, by view. */
  const step = (direction: -1 | 1) => {
    if (view === "days") {
      setMonth(
        new Date(month.getFullYear(), month.getMonth() + direction, 1)
      );
      return;
    }
    if (view === "months") {
      setMonth(
        new Date(month.getFullYear() + direction, month.getMonth(), 1)
      );
      return;
    }
    setYearStart((current) => current + direction * YEAR_PAGE);
  };

  /**
   * The calendar itself: the month nav, the grid, and the way back to today.
   *
   * Lifted clear of the box around it, because a phone and a desktop put the
   * same calendar in two very different ones — see `panel`.
   */
  const calendar = (
    <>
      {/* The month nav stays put and the grid under it scrolls, so a
          panel squeezed by a short screen can still be paged through a
          month at a time — the one control that must not be the thing
          scrolled out of reach. */}
      <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Previous"
          onClick={() => step(-1)}
          className={navButtonClasses}
        >
          <ChevronLeft aria-hidden="true" className="h-4 w-4" />
        </button>

        {view === "years" ? (
          <span className="text-[14px] font-medium text-ink">
            {yearStart} - {yearStart + YEAR_PAGE - 1}
          </span>
        ) : (
          <div className="flex items-center gap-1">
            {view === "days" && (
              <button
                type="button"
                onClick={() => setView("months")}
                className={`${headerLabelClasses} text-ink`}
              >
                {MONTHS[month.getMonth()]}
              </button>
            )}
            <button
              type="button"
              onClick={() => setView("years")}
              className={`${headerLabelClasses} text-brand-dark`}
            >
              {month.getFullYear()}
            </button>
          </div>
        )}

        <button
          type="button"
          aria-label="Next"
          onClick={() => step(1)}
          className={navButtonClasses}
        >
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar-overlay">
        {view === "days" && renderDays()}
        {view === "months" && renderMonths()}
        {view === "years" && renderYears()}
      </div>

      {/* Under the grid rather than beside the month nav, and outside
          the scroller: it is a way out of wherever the panel has been
          walked to, so it has to stay put while the grid moves under it.
          `shrink-0`, or a short screen squeezing the panel would take
          the one control that undoes the squeeze.

          It picks rather than merely paging the calendar back. Somebody
          who has walked three months out and reaches for "today" wants
          the day, not the month it is in with the day still to find. */}
      {showToday && !outOfRange(new Date()) && (
        <div className="mt-2 shrink-0 border-t border-line pt-2">
          <button
            type="button"
            onClick={() => pick(new Date())}
            className="w-full rounded-md py-1.5 max-md:py-3 text-[13px] font-medium text-brand-dark transition-colors hover:bg-brand-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            Go to today
          </button>
        </div>
      )}
    </>
  );

  /**
   * The box the calendar is drawn in, portalled out to the body.
   *
   * It has to be portalled whichever shape it takes: these fields sit inside
   * scroll containers and bottom sheets, and an absolutely positioned panel
   * would be clipped by the nearest of them. Portalled, it is
   * `position: fixed`, which is why the trigger is measured in viewport
   * coordinates and re-measured whenever anything moves — see
   * `useAnchoredPanel`.
   *
   * `z-[60]` puts it over `BottomSheet`'s own `z-50`, so a date field near the
   * foot of a sheet opens its calendar over the sheet rather than behind it.
   *
   * ## Two shapes, and why the phone does not get the desktop's
   *
   * From `md` up it hangs off the control that opened it, which is what a
   * reader expects of a calendar beside a field.
   *
   * Below `md` it comes up from the bottom edge at the full width of the
   * screen instead. A 288px card hung off a field is a desktop calendar
   * shrunk onto a phone: it lands wherever the field happens to be — halfway
   * up the screen, over the very fields it is answering, with a month of
   * 32px circles floating in the middle of it and a strip of the form showing
   * down both sides. The second screenshot of the booking form is exactly
   * that. Full width and anchored to the bottom, the month is drawn at the
   * size of a thumb, the grid uses the width the phone actually has, and the
   * calendar arrives where every other choice on this app's phone screens
   * arrives — see `BottomSheet`, whose corners, dimming and animation this
   * borrows rather than inventing a second drawer.
   *
   * Not literally a `BottomSheet`: that one sets `InsideSheetContext`, which
   * tells pickers underneath it not to open a sheet of their own, and this
   * panel is routinely opened from inside one — a date field on the booking
   * sheet would otherwise put the context back the wrong way round.
   *
   * No press handler on the dimming: the document-level `mousedown` above
   * already closes the panel on anything outside it, and the backdrop is
   * outside it.
   */
  const panel = !open ? null : isMobile ? (
    createPortal(
      <div className="fixed inset-0 z-[60] flex items-end justify-center bg-[#0B1B1F]/45 backdrop-blur-[3px] animate-fade-in">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Choose a date"
          // 20px under the grid over the safe-area inset, which is what every
          // sheet in the app keeps under its last row — see `BottomSheet`.
          className="flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-[16px] bg-white px-4 pb-[calc(env(safe-area-inset-bottom)_+_1.25rem)] shadow-[0_-16px_48px_rgba(11,27,31,0.24)] animate-sheet-in"
        >
          {/* The grabber, as on every other drawer: a handle to the eye and
              nothing to the hand, so it is not announced. */}
          <div aria-hidden="true" className="flex justify-center pt-3 pb-3">
            <span className="h-[5px] w-10 rounded-full bg-[#DFE6EA]" />
          </div>
          {calendar}
        </div>
      </div>,
      document.body
    )
  ) : rect
      ? createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Choose a date"
            className="z-[60] flex flex-col rounded-md border border-line bg-white p-3 shadow-[0_8px_24px_rgba(11,27,31,0.12)]"
            style={{
              // The shared placement, which is what caps the panel to the room
              // actually in front of it. This used to be written out here and
              // set no height at all: on a short laptop screen — a 13" Mac with
              // the dialog already filling it — neither side of the field has
              // 348px, so the panel flipped to the larger one and overflowed it
              // anyway, with the last fortnight of the month off the bottom
              // edge and no way to scroll to it.
              ...anchoredPanelStyle(rect, openUpward, PANEL_HEIGHT, PANEL_WIDTH),
              // Clamped to the viewport: the field can sit in the last column
              // of the grid, where its left edge leaves no room for the panel.
              // Its own line because the shared style hangs a panel off the
              // trigger's left edge, which is right for a list that is the
              // width of its field and wrong for a calendar that is not.
              left: Math.max(
                8,
                Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)
              ),
            }}
          >
            {calendar}
          </div>,
          document.body
        )
      : null;

  // The whole control, where there is no field: the same button the field
  // carries on its right, grown to the height of the things it stands beside
  // and tinted, because on its own it is the control rather than a way into
  // one. It keeps its own pressed state, so the strip reads as a row of
  // buttons of which this is the eighth.
  if (iconOnly) {
    return (
      <div ref={anchorRef} className={`relative ${className}`}>
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          disabled={disabled}
          // Ahead of focus, so pressing it while the panel is open shuts it
          // rather than closing and immediately reopening.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => (open ? close() : openPanel())}
          className={`flex h-full w-full items-center justify-center rounded-[6px] border p-2.5 3xl:p-3 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            open
              ? "border-brand bg-[#D7F9FD] text-[#00A5B4]"
              : "border-[#A6EDF5] bg-[#EFFBFD] text-[#00A5B4] hover:bg-[#D7F9FD]"
          }`}
          {...aria}
        >
          <CalendarIcon className="h-[18px] w-[18px] 3xl:h-5 3xl:w-5" />
        </button>

        {panel}
      </div>
    );
  }

  return (
    <div ref={anchorRef} className={`relative ${className}`}>
      <div
        className={
          "flex w-full items-center gap-2 rounded-[6px] border bg-white px-3.5 py-2.5 3xl:py-3 " +
          "transition-colors " +
          (disabled ? "cursor-not-allowed bg-[#F7F9FA] " : "") +
          (invalid
            ? "border-red-400 focus-within:border-red-500"
            : "border-line focus-within:border-brand")
        }
      >
        {/* Still an input: a date of birth is quicker typed than hunted for,
            and the normaliser types the slashes as it goes. The calendar is
            the other way in, not a replacement. */}
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="bday"
          maxLength={10}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onChange={(event) =>
            onValueChange(
              normalise ? normalise(event.target.value) : event.target.value
            )
          }
          onClick={openPanel}
          onKeyDown={handleKeyDown}
          // Only when the panel is shut — otherwise `close` reports it, once —
          // and never for the blur that opens the panel. See `opening`.
          onBlur={() => !open && !opening.current && onBlur?.()}
          className="w-full bg-transparent text-[14px] 3xl:text-[15px] text-ink placeholder:text-ink-subtle focus:outline-none disabled:cursor-not-allowed disabled:text-ink-muted"
          {...aria}
        />
        <button
          type="button"
          aria-label="Open calendar"
          aria-expanded={open}
          disabled={disabled}
          // Ahead of focus, so pressing the icon while the panel is open shuts
          // it rather than closing and immediately reopening on focus.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => (open ? close() : openPanel())}
          className="flex-shrink-0 text-ink-muted transition-colors hover:text-ink disabled:cursor-not-allowed"
        >
          <CalendarIcon className="h-4 w-4" />
        </button>
      </div>

      {panel}
    </div>
  );
};

export default DatePicker;
